'use client';
import { useCallback, useEffect, useState } from 'react';
import { Badge, Box, Fab } from '@mui/material';
import { useCurrentLocale } from '@peerless/ui-locale';

import LauncherImage from '@peerless/ui-assets/images/peerless-av-chat-bubble-image.png';

const zendeskKeys = {
  US: process.env.NEXT_PUBLIC_ZENDESK_US ?? '',
  UK: process.env.NEXT_PUBLIC_ZENDESK_UK ?? '',
  DE: process.env.NEXT_PUBLIC_ZENDESK_DE ?? '',
  MX: process.env.NEXT_PUBLIC_ZENDESK_MX ?? '',
  FR: process.env.NEXT_PUBLIC_ZENDESK_FR ?? '',
};

export function ZendeskLauncher() {
  const { region } = useCurrentLocale();
  const [unreadMessages, setUnreadMessages] = useState(0);

  const key = zendeskKeys[region];

  const { isLoaded } = useZendesk(key);

  useEffect(() => {
    if (!isLoaded) return;

    window.zE('messenger:on', 'unreadMessages', (count: number) => {
      setUnreadMessages(count);
    });
  }, [isLoaded]);

  const open = useCallback(() => {
    if (!isLoaded) return;

    window.zE('messenger', 'open');
  }, [isLoaded]);

  if (!key || !isLoaded) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1300,
      }}
    >
      <Badge
        color="primary"
        badgeContent={unreadMessages}
        overlap="circular"
        sx={{
          '& .MuiBadge-badge': { zIndex: 1301 },
        }}
      >
        <Fab
          onClick={open}
          sx={{
            backgroundImage: `url(${LauncherImage.src})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        ></Fab>
      </Badge>
    </Box>
  );
}

function useZendesk(key: string): { isLoaded: boolean } {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!key) return;

    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.type = 'text/javascript';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;
    script.async = true;
    script.onload = () => setIsLoaded(true);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      setIsLoaded(false);
    };
  }, [key]);

  return { isLoaded };
}
